import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/lib/hooks";

const fmt = (n) => (n == null ? null : Number(n).toLocaleString());

export const ListingCourtesy = ({ courtesy, testid = "listing-courtesy" }) =>
  courtesy?.listingUrl ? (
    <p className="text-xs text-[var(--stone-dark)]" data-testid={testid}>
      {courtesy.brokerage || courtesy.source ? `Listing courtesy of ${courtesy.brokerage || courtesy.source}` : "Listing"} · <a href={courtesy.listingUrl} target="_blank" rel="noreferrer noopener" className="underline">view the listing</a>
    </p>
  ) : null;

const Retrieving = ({ st }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const secs = Math.max(0, Math.round((now - new Date(st.startedAt || Date.now()).getTime()) / 1000));
  return (
    <div className="mt-8 rounded-[4px] bg-[var(--bone-2)] p-5" data-testid="listing-retrieving">
      <div className="flex items-center gap-2 text-sm font-medium"><Loader2 size={16} className="animate-spin" /> Retrieving listing details…</div>
      <p className="mt-1 text-sm text-[var(--stone-dark)]" data-testid="listing-retrieving-stage">{st.stage || "Starting"} · {secs}s</p>
      <div className="mt-3 h-1 overflow-hidden rounded bg-[var(--sand)]"><div className="h-full bg-[var(--charcoal)] transition-[width] duration-1000" style={{ width: `${Math.min(95, (secs / 60) * 100)}%` }} /></div>
      <p className="mt-2 text-xs text-[var(--stone-dark)]">Usually 20–60 seconds (building pages can take up to 2 minutes). Safe to refresh — retrieval keeps running.</p>
    </div>
  );
};

export const ListingFacts = ({ prop }) => {
  const f = prop.facts;
  if (!f) return null;
  const le = f.lastEvent;
  const rows = [["Price", f.price != null && `$${fmt(f.price)}`], ["Last event", !f.price && le?.event && `${le.event}${le.price ? ` · $${fmt(le.price)}` : ""}${le.date ? ` · ${le.date}` : ""}`], ["Beds", fmt(f.bedrooms)], ["Baths", fmt(f.bathrooms)], ["Living area", f.livingArea != null && `${fmt(f.livingArea)} ${f.livingAreaUnits || "sq ft"}`],
    ["Year built", f.yearBuilt], ["Home type", f.homeType && f.homeType.replace(/_/g, " ").toLowerCase()], ["Status", f.listingStatus && f.listingStatus.replace(/_/g, " ").toLowerCase()]].filter((r) => r[1]);
  return (
    <section className="mt-6 border-y border-[var(--line)] py-5" data-testid="listing-facts">
      {f.address && <p className="font-display text-xl" data-testid="listing-facts-address">{f.address}</p>}
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
        {rows.map(([k, v]) => <div key={k} data-testid={`listing-fact-${k.toLowerCase().replace(/ /g, "-")}`}><dt className="eyebrow">{k}</dt><dd className="text-sm capitalize">{v}</dd></div>)}
      </dl>
      <p className="mt-3 text-xs text-[var(--stone-dark)]" data-testid="listing-facts-source">{f.source} · {new Date(f.retrievedAt).toLocaleString()}</p>
      <ListingCourtesy courtesy={prop.listingCourtesy} />
    </section>
  );
};

export const ListingRetrieval = ({ prop, reload, onImported, fallback }) => {
  const st = prop.listingRetrieval;
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  usePoll(reload, st.status === "retrieving", 2000);
  const loadPhotos = useCallback(() => api.get(`/properties/${prop.id}/listing-photos`).then((r) => { setLoadErr(null); setData(r.data); setSel((s) => (s.length || !r.data.photos.length ? s : [r.data.photos[0].sourceId])); }).catch((e) => { setLoadErr(errMsg(e, "We couldn't load the listing photos.")); setData({ photos: [], photoCount: 0 }); }), [prop.id]);
  useEffect(() => { if (st.status === "done") loadPhotos(); }, [st.status, loadPhotos]);
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const retry = async () => { try { await api.post(`/properties/${prop.id}/listing/retry`); reload(); } catch (e) { toast(errMsg(e, "Try again in a moment.")); } };
  const decline = async () => { await api.post(`/properties/${prop.id}/listing-photos/decline`); reload(); };
  const reimagine = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/properties/${prop.id}/listing-photos/import`, { photoIds: sel });
      toast(r.data.message || `${sel.length} photo${sel.length === 1 ? "" : "s"} added with your recorded confirmation`);
      onImported();
    } catch (e) { toast(errMsg(e, "Couldn't add those photos.")); }
    setBusy(false);
  };

  if (st.status === "retrieving") return <Retrieving st={st} />;
  if (st.status === "limited") return <div className="mt-8" data-testid="listing-limited"><p className="font-display text-2xl">{st.message}</p>{fallback}</div>;
  if (st.status === "failed") {
    return (
      <div className="mt-8" data-testid="listing-failed">
        <p className="font-display text-2xl">We couldn&apos;t retrieve this listing.</p>
        <button className="btn-ghost mt-3" onClick={retry} data-testid="listing-retry-button">Try again</button>
        {fallback}
      </div>
    );
  }
  if (st.declined) return <div className="mt-8" data-testid="listing-declined"><p className="font-display text-2xl" data-testid="listing-declined-message">Upload photos you&apos;re authorized to use</p>{fallback}</div>;
  if (!data) return <div className="mt-8 flex items-center gap-2 text-sm"><Loader2 size={15} className="animate-spin" /> Loading the listing…</div>;
  if (!data.photoCount) return loadErr ? (
    <div className="mt-8" data-testid="listing-load-error"><p className="font-display text-2xl">{loadErr}</p>
      <button className="btn-ghost mt-3" onClick={loadPhotos} data-testid="listing-load-retry-button">Try again</button>{fallback}</div>
  ) : fallback;
  if (!data.attestation?.transform) return <RightsStep prop={prop} data={data} onDone={loadPhotos} onDecline={decline} />;
  return (
    <section className="mt-8 rounded-[4px] bg-[var(--bone-2)] p-5 sm:p-7" data-testid="listing-photos-section">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="eyebrow">Listing photos · pick a room</div>
        <span className="inline-flex items-center gap-1 text-xs text-[var(--stone-dark)]" data-testid="listing-photos-private"><Lock size={12} /> Private until you share</span>
      </div>
      <p className="mt-1 text-xs text-[var(--stone-dark)]" data-testid="listing-attested-line">Rights confirmed by you · {new Date(data.attestation.createdAt).toLocaleString()} · {data.courtesyLine}</p>
      <div className="mt-4 flex items-center justify-between text-sm">
        <span data-testid="listing-photos-count">{sel.length} of {data.photos.length} selected</span>
        <button className="btn-quiet" onClick={() => setSel(sel.length === data.photos.length ? [] : data.photos.map((p) => p.sourceId))} data-testid="listing-select-all-button">{sel.length === data.photos.length ? "Clear" : "Select all"}</button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="listing-photos-grid">
        {data.photos.map((p) => {
          const on = sel.includes(p.sourceId);
          return (
            <button key={p.sourceId} type="button" onClick={() => toggle(p.sourceId)} data-testid={`listing-photo-${p.sourceId.replace(":", "-")}`} data-selected={on}
              className={`relative aspect-[4/3] overflow-hidden rounded-[2px] bg-[var(--sand)] ${on ? "ring-2 ring-[var(--charcoal)] ring-offset-2 ring-offset-[var(--bone-2)]" : ""}`}>
              <img src={p.thumbUrl} alt={p.title} loading="lazy" referrerPolicy="no-referrer" className="h-full w-full object-cover" />
              {on && <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[var(--charcoal)] text-[var(--bone)]"><Check size={15} /></span>}
            </button>
          );
        })}
      </div>
      <div className="sticky bottom-3 mt-4 flex flex-wrap justify-end gap-2">
        <button className="btn-primary shadow-lg" disabled={!sel.length || busy} onClick={reimagine} data-testid="listing-reimagine-selected-button">{busy && <Loader2 size={15} className="animate-spin" />} Reimagine selected{sel.length ? ` (${sel.length})` : ""}</button>
      </div>
    </section>
  );
};

const RightsStep = ({ prop, data, onDone, onDecline }) => {
  const [t, setT] = useState(false);
  const [pub, setPub] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/properties/${prop.id}/listing-photos/attest`, { transform: t, publish: pub }); await onDone(); } catch (e) { toast(errMsg(e, "Couldn't record your confirmation.")); }
    setBusy(false);
  };
  const Box = ({ on, set, text, id }) => (
    <label className="flex min-h-[48px] cursor-pointer items-start gap-3 rounded-[3px] border border-[var(--line)] bg-white/50 p-3 text-sm">
      <input type="checkbox" className="mt-0.5 h-4 w-4" checked={on} onChange={(e) => set(e.target.checked)} data-testid={id} /> <span>{text}</span>
    </label>
  );
  return (
    <section className="mt-8 rounded-[4px] bg-[var(--bone-2)] p-5 sm:p-7" data-testid="listing-rights-step">
      <div className="eyebrow">{data.photoCount} listing photos retrieved</div>
      <h3 className="mt-1 font-display text-2xl leading-snug">Confirm your rights to use these photos</h3>
      <p className="mt-1 text-sm text-[#B42318]" data-testid="listing-rights-label">{data.label}</p>
      <div className="mt-4 grid gap-2">
        <Box on={t} set={setT} text={data.permissions.transform} id="rights-transform-checkbox" />
        <Box on={pub} set={setPub} text={data.permissions.publish} id="rights-publish-checkbox" />
      </div>
      <p className="mt-3 text-xs text-[var(--stone-dark)]" data-testid="rights-note">{data.note}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button className="btn-primary" disabled={!t || busy} onClick={confirm} data-testid="rights-confirm-button">{busy && <Loader2 size={15} className="animate-spin" />} Confirm and choose photos</button>
        <button className="btn-quiet" onClick={onDecline} data-testid="rights-decline-button">I don&apos;t have these rights</button>
      </div>
    </section>
  );
};
